import { MesfModal } from "@dexteel/mesf-core";
import { ArrowForward } from "@mui/icons-material";
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { formatDateTime } from "../utils/calendarUtils";

const MODAL_CONTENT_WIDTH = 1040;

const NOTICE_TONES = {
	error: { backgroundColor: "#FDECEA", color: "#C62828" },
	warning: { backgroundColor: "#FFF3E0", color: "#E65100" },
} as const;

export const EventChangeNotice = ({
	tone,
	children,
}: {
	tone: keyof typeof NOTICE_TONES;
	children: ReactNode;
}) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
			mb: 2,
			px: 2,
			py: 1,
			borderRadius: 2,
			fontWeight: 600,
			fontSize: "0.85rem",
			...NOTICE_TONES[tone],
		}}
	>
		{children}
	</Box>
);

const TimeBox = ({
	label,
	value,
	muted,
}: {
	label: string;
	value: Date;
	muted?: boolean;
}) => (
	<Box sx={{ textAlign: "center" }}>
		<Typography
			variant="caption"
			color="text.secondary"
			sx={{ display: "block", mb: 0.25 }}
		>
			{label}
		</Typography>
		<Box
			sx={{
				px: 1.5,
				py: 0.75,
				borderRadius: 1.5,
				backgroundColor: muted ? "grey.100" : "grey.200",
				color: muted ? "text.disabled" : "text.primary",
				fontWeight: muted ? 500 : 700,
				fontSize: "0.9rem",
				whiteSpace: "nowrap",
			}}
		>
			{formatDateTime(value)}
		</Box>
	</Box>
);

const Window = ({
	label,
	start,
	end,
	placeholder,
	muted,
}: {
	label: string;
	start?: Date | null;
	end?: Date | null;
	placeholder?: string;
	muted?: boolean;
}) => (
	<Box>
		<Typography
			variant="subtitle2"
			fontWeight={700}
			sx={{ display: "block", mb: 1 }}
		>
			{label}
		</Typography>
		{start && end ? (
			<Box
				sx={{
					display: "flex",
					alignItems: "flex-end",
					gap: 1,
					flexWrap: "wrap",
				}}
			>
				<TimeBox label="Start" value={start} muted={muted} />
				<Typography
					color={muted ? "text.disabled" : "text.secondary"}
					sx={{ pb: 0.75 }}
				>
					-
				</Typography>
				<TimeBox label="End" value={end} muted={muted} />
			</Box>
		) : (
			<Typography variant="body2" color="text.secondary">
				{placeholder}
			</Typography>
		)}
	</Box>
);

type Props = {
	title: string;
	actionLabel: string;
	confirmLabel: string;
	/** what is changing, as a full-width summary card (it carries the duration) */
	summary: ReactNode;
	oldStart?: Date | null;
	oldEnd?: Date | null;
	oldPlaceholder?: string;
	newStart: Date;
	newEnd: Date;
	loading?: boolean;
	disabled?: boolean;
	children?: ReactNode;
	actions?: ReactNode;
	onConfirm: () => void;
	onCancel: () => void;
};

export const ConfirmEventChangeModal = ({
	title,
	actionLabel,
	confirmLabel,
	summary,
	oldStart,
	oldEnd,
	oldPlaceholder = "New event",
	newStart,
	newEnd,
	loading = false,
	disabled = false,
	children,
	actions,
	onConfirm,
	onCancel,
}: Props) => (
	<MesfModal
		open
		handleClose={() => !loading && onCancel()}
		title={title}
		maxWidth="lg"
	>
		{/* MesfModal sizes to its content: a fixed width keeps long part numbers
		    whole, capped by the dialog on small screens */}
		<MesfModal.Content
			dividers
			sx={{
				overflow: "auto",
				width: MODAL_CONTENT_WIDTH,
				maxWidth: "100%",
				boxSizing: "border-box",
			}}
		>
			<Box sx={{ p: 1 }}>
				<Box>
					<Typography
						variant="subtitle2"
						fontWeight={700}
						sx={{ display: "block", mb: 1 }}
					>
						{actionLabel}
					</Typography>
					{summary}
				</Box>
				<Divider sx={{ my: 2.5 }} />
				<Box
					sx={{
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
						gap: 2,
						mb: 2.5,
						p: 2,
						backgroundColor: "action.hover",
						borderRadius: 2,
						flexWrap: "wrap",
					}}
				>
					<Window
						label="From"
						start={oldStart}
						end={oldEnd}
						placeholder={oldPlaceholder}
						muted
					/>
					<ArrowForward color="primary" sx={{ alignSelf: "center", mt: 3 }} />
					<Window label="To" start={newStart} end={newEnd} />
				</Box>
				{children}
			</Box>
		</MesfModal.Content>
		<MesfModal.Actions sx={{ px: 3, py: 2 }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					width: "100%",
					gap: 1,
				}}
			>
				<Button variant="outlined" onClick={onCancel} disabled={loading}>
					Cancel
				</Button>
				{actions}
				<Button
					variant="contained"
					color="primary"
					onClick={onConfirm}
					disabled={loading || disabled}
					startIcon={
						loading ? <CircularProgress size={16} color="inherit" /> : null
					}
				>
					{confirmLabel}
				</Button>
			</Box>
		</MesfModal.Actions>
	</MesfModal>
);
